import React, { useEffect, useState } from "react";
import { APIProvider, Map } from "@vis.gl/react-google-maps";
import { Card, Button, Modal, Form, Space, Switch } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { ParkingDrawingTools } from "./components/ParkingDrawingTools";
import { ExistingParkingPolygons } from "./components/ExistingParkingPolygons";
import { ParkingTable } from "./components/ParkingTable";
import { ParkingForm } from "./components/ParkingForm";
import { useParkingManagement } from "./hooks/useParkingManagement";
import { getParkingLines } from "../../api/parkpass/parkpassParkings";
import PermissionRender from "../../components/PermissionRender";

const ParkingManagementPage = () => {
  const [form] = Form.useForm();
  const [showArchived, setShowArchived] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [editingLines, setEditingLines] = useState([]); // Store existing lines for future edit functionality
  const {
    parkings,
    currentParking,
    isModalVisible,
    loading,
    drawingMode,
    editingParking,
    isEditing,
    zoomToParking,
    fetchParkings,
    handlePolygonComplete,
    startEditingParking,
    handlePolygonEdit,
    handleMarkerEdit,
    handleViewParking,
    cancelEditing,
    handleSave,
    handleModalClose,
    handleArchive,
    handleUnarchive,
    setDrawingMode,
    setIsModalVisible,
  } = useParkingManagement(form);

  useEffect(() => {
    fetchParkings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load lines when editing a parking
  useEffect(() => {
    const loadLinesForEditing = async () => {
      if (editingParking && editingParking.id) {
        try {
          const existingLines = await getParkingLines(editingParking.id);
          const linesArray = existingLines.map((line) => ({
            external_id: line.external_id,
            name: line.name,
            id: line.id, // Keep the line ID for potential updates
          }));
          setEditingLines(existingLines);

          // Update form if modal is visible
          if (isModalVisible) {
            form.setFieldsValue({
              lines: linesArray,
            });
          }
        } catch (error) {
          console.error("Error fetching parking lines:", error);
        }
      }
    };

    loadLinesForEditing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingParking?.id, isModalVisible]);

  const openEditModal = async () => {
    if (editingParking) {
      // Convert tariff_info object to array format for Form.List
      const tariffArray = editingParking.tariff_info
        ? Object.entries(editingParking.tariff_info).map(([key, value]) => ({
            key,
            value,
          }))
        : [];

      // Use already loaded lines from editingLines state
      const linesArray = editingLines.map((line) => ({
        external_id: line.external_id,
        name: line.name,
        id: line.id,
      }));

      form.setFieldsValue({
        name: editingParking.name,
        name_ar: editingParking.name_ar,
        name_fr: editingParking.name_fr,
        parking_type: editingParking.parking_type,
        external_id: editingParking.external_id,
        tariff_info: tariffArray,
        lines: linesArray,
        notes: editingParking.notes,
        archived: editingParking.archived || false,
      });
      setIsModalVisible(true);
    }
  };

  // Filter parkings based on archived toggle
  const filteredParkings = showArchived
    ? parkings
    : parkings.filter((parking) => !parking.archived);

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} region="MA">
      <div style={{ padding: 24 }}>
        <Card
          title="Gestion des Parkings"
          extra={
            <Space>
              <span>Afficher les archivés:</span>
              <Switch
                checked={showArchived}
                onChange={(checked) => setShowArchived(checked)}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
              <PermissionRender
                requiredPermission={["citizen_parkings", "create"]}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setDrawingMode(true)}
                  disabled={isEditing}
                >
                  Dessiner sur carte
                </Button>
              </PermissionRender>
            </Space>
          }
        >
          {isEditing && (
            <div
              style={{
                marginBottom: 16,
                backgroundColor: "#fff2f0",
                padding: 16,
                borderRadius: 4,
              }}
            >
              <Space>
                <span>Parking en cours d'édition : {editingParking.name}</span>
                <Button type="primary" onClick={openEditModal}>
                  Enregistrer les modifications
                </Button>
                <Button danger onClick={cancelEditing}>
                  Annuler
                </Button>
              </Space>
            </div>
          )}

          <div style={{ height: "50vh", marginBottom: 24 }}>
            <Map
              disableDefaultUI={false}
              options={{
                mapTypeControl: true,
                zoomControl: true,
                streetViewControl: false,
              }}
              defaultCenter={{ lat: 35.774469, lng: -5.798669 }}
              defaultZoom={15}
              gestureHandling="greedy"
              styles={[
                {
                  featureType: "poi",
                  elementType: "labels",
                  stylers: [{ visibility: "on" }],
                },
              ]}
            >
              {drawingMode && (
                <ParkingDrawingTools
                  onPolygonComplete={handlePolygonComplete}
                  parkingType={form.getFieldValue("parking_type")}
                />
              )}
              <ExistingParkingPolygons
                parkings={filteredParkings}
                editingParking={editingParking}
                onPolygonEdit={handlePolygonEdit}
                onMarkerEdit={handleMarkerEdit}
                zoomToParking={zoomToParking}
                isEditing={isEditing}
              />
            </Map>
          </div>

          <ParkingTable
            parkings={filteredParkings}
            loading={loading}
            editingParking={editingParking}
            isEditing={isEditing}
            onView={handleViewParking}
            onEdit={startEditingParking}
            onSave={openEditModal}
            onCancel={cancelEditing}
            onArchive={handleArchive}
            onUnarchive={handleUnarchive}
          />
        </Card>

        <Modal
          title={
            editingParking
              ? "Enregistrer les modifications du parking"
              : "Créer un nouveau parking"
          }
          open={isModalVisible}
          onOk={form.submit}
          onCancel={handleModalClose}
          confirmLoading={loading}
          width={600}
        >
          <ParkingForm
            form={form}
            onFinish={handleSave}
            editingParking={editingParking}
            hasMapData={
              !!editingParking?.coordinates || !!editingParking?.marker
            }
            currentParking={currentParking}
          />
        </Modal>
      </div>
    </APIProvider>
  );
};

export default ParkingManagementPage;
